import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Typography } from "@mui/material";
import type { CurrencyCode } from "Models/CurrencyCodes";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  type CreateMerchantFacilityParameters,
  useCreateMerchantFacilityMutation,
} from "Api/Mutations/Merchants/useCreateMerchantFacilityMutation";
import {
  type UpdateMerchantFacilityParameters,
  useUpdateMerchantFacilityMutation,
} from "Api/Mutations/Merchants/useUpdateMerchantFacilityMutation";
import { useUploadFacilityLogoMutation } from "Api/Mutations/Merchants/useUploadFacilityLogoMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import {
  AddressUncommittedProvider,
  useAddressUncommittedTracker,
} from "Components/Shared/Inputs/Form/AddressUncommittedContext";
import { BasicInformationSection } from "Components/Shared/Merchant/Facilities/Sections/BasicInformationSection";
import { ContactSection } from "Components/Shared/Merchant/Facilities/Sections/ContactSection";
import { PublicFacilityInfoSection } from "Components/Shared/Merchant/Facilities/Sections/PublicFacilityInfoSection";
import { AddressSection } from "Components/Shared/Merchant/Facilities/Sections/AddressSection";
import { TurnoverSection } from "Components/Shared/Merchant/Facilities/Sections/TurnoverSection";
import { TerminalSection } from "Components/Shared/Merchant/Facilities/Sections/TerminalSection";
import { PhotoSection } from "Components/Shared/Merchant/Facilities/Sections/PhotoSection";
import { useFacilityPhotos } from "Components/Shared/Merchant/Facilities/hooks/useFacilityPhotos";
import {
  type FacilityFormModel,
  type FacilitySection,
  getMerchantFacilitySchema,
} from "Components/Shared/Merchant/Facilities/schema";
import { useFacilitySectionCompletion } from "Components/Shared/Merchant/Facilities/useFacilitySectionCompletion";
import { Resources, useResource } from "Translations/Resources";
import {
  AddressType,
  type AddressDto,
  type ApiProblemDetails,
  type CreateMerchantFacilityDto,
  type MerchantFacilityDto,
} from "Infrastructure/Api/Api";
import { EMPTY_PHONE } from "Utils/PhoneUtils";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { facilityDtoToFormModel } from "Components/Shared/Merchant/Facilities/utils";
import { useFacilityCategoryList } from "Hooks/Options/useFacilityCategoryList";
import {
  type ContactSource,
  type FacilityFormVariant,
  SECTION_FIELDS_MAP,
  SECTION_ORDER,
} from "Components/Shared/Merchant/Facilities/constants";
import { FacilityLogoUpload } from "Components/Shared/Merchant/Facilities/FacilityLogoUpload";
import { PendingLogoPicker } from "Components/Shared/Merchant/PendingLogoPicker";
import { useContactSources } from "Components/Shared/Merchant/Facilities/hooks/useContactSources";
import { useReserveTerminalsMutation } from "Api/Mutations/Merchants/useReserveTerminalsMutation";
import { useGetHunterTerminalsForReservationQuery } from "Api/Queries/Merchants/useGetHunterTerminalsForReservationQuery";

const RESOURCES = Resources.Components.Merchant.Facilities.Form;
const LOGO_RESOURCES = Resources.Components.Merchant.Logo;
const FACILITY_TERMINAL_RESOURCES =
  Resources.Components.Merchant.OrbitL.FacilityTerminals;

const getReserveErrorResource = (error: unknown) => {
  const apiErrors = (error as { data?: ApiProblemDetails })?.data?.errors;
  if (apiErrors && "TerminalInventoryItem" in apiErrors) {
    return FACILITY_TERMINAL_RESOURCES.InsufficientStock;
  }
  if (apiErrors && "TerminalCount" in apiErrors) {
    return FACILITY_TERMINAL_RESOURCES.CountsLocked;
  }
  return RESOURCES.UpdateError;
};

export type Props = {
  facility?: Partial<MerchantFacilityDto>;
  merchantPublicID: string;
  onSaveSuccessful: () => void;
  /** A brand-new facility exists on the server but the form should stay open for it. */
  onFacilityCreated?: (facility: MerchantFacilityDto) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  variant?: FacilityFormVariant;
  companyHeadquartersAddress?: AddressDto;
  partyPublicID?: string | null;
  requiresPhotos?: boolean;
  hasComgateProduct?: boolean;
  hasOrbitLProduct?: boolean;
  hasSoftPosProduct?: boolean;
  isHunterFinanced?: boolean;
  // Merchant's market currency — the default for a NEW facility's turnover inputs.
  // An existing facility uses its own stored TurnoverCurrencyCode instead.
  marketCurrencyCode?: CurrencyCode | string;
};

export const FacilityForm = ({
  facility,
  merchantPublicID,
  onSaveSuccessful,
  onFacilityCreated,
  onDirtyChange,
  variant = "complete",
  companyHeadquartersAddress,
  partyPublicID,
  hasComgateProduct,
  hasOrbitLProduct,
  hasSoftPosProduct,
  isHunterFinanced,
  marketCurrencyCode,
}: Props) => {
  const [expandedSection, setExpandedSection] =
    useState<FacilitySection | null>("basicInformation");
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [isLogoChanged, setIsLogoChanged] = useState(false);
  const [selectedTerminalIDs, setSelectedTerminalIDs] = useState<Array<string>>(
    [],
  );

  const shouldFetchTerminals = !!hasComgateProduct && !!isHunterFinanced;
  const {
    data: hunterTerminalsData,
    isLoading: isLoadingTerminals,
    isError: isTerminalsError,
  } = useGetHunterTerminalsForReservationQuery(
    merchantPublicID,
    facility?.publicID,
    shouldFetchTerminals,
  );
  const hunterTerminals = hunterTerminalsData?.terminals ?? [];
  const assignedTerminals = facility?.publicID
    ? (hunterTerminalsData?.assignedTerminals ?? [])
    : [];
  const totalRequiredCount = hunterTerminalsData?.totalRequiredCount ?? 0;
  const alreadyAssignedCount = hunterTerminalsData?.alreadyAssignedCount ?? 0;
  const allTerminals = useMemo(
    () => [...assignedTerminals, ...hunterTerminals],
    [assignedTerminals, hunterTerminals],
  );
  const assignedTerminalIDs = useMemo(
    () => assignedTerminals.map(({ publicID }) => publicID),
    [assignedTerminals],
  );
  const availableStock = hunterTerminals.length;
  const totalAvailableStock = availableStock + alreadyAssignedCount;

  const hasInitializedTerminals = useRef(false);
  useEffect(() => {
    if (assignedTerminals.length > 0 && !hasInitializedTerminals.current) {
      hasInitializedTerminals.current = true;
      setSelectedTerminalIDs(
        assignedTerminals.map((terminal) => terminal.publicID),
      );
    }
  }, [assignedTerminals]);

  const { t } = useResource();
  const { hasUncommittedAddress, uncommittedContextValue } =
    useAddressUncommittedTracker();
  const { categoryOptions: facilityCategoryOptions } =
    useFacilityCategoryList();
  const defaultValues = facility
    ? facilityDtoToFormModel(facility)
    : { phone: EMPTY_PHONE };
  const schema = getMerchantFacilitySchema(t, {
    hasOrbitLProduct,
    hasSoftPosProduct,
  });
  const formMethods = useForm<FacilityFormModel>({
    resolver: zodResolver(schema),
    defaultValues,
  });
  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    trigger,
    formState: { isDirty, errors, isSubmitted },
  } = formMethods;

  const watchedTerminalCount = watch("terminalCount") ?? 0;
  const watchedSoftPosCount = watch("softPosCount") ?? 0;
  const savedTerminalCount = facility?.terminalCount ?? 0;
  const effectiveTotalRequired =
    totalRequiredCount - savedTerminalCount + watchedTerminalCount;
  const isLegione = !!hasComgateProduct && !isHunterFinanced;
  const isStockExceeded =
    shouldFetchTerminals && effectiveTotalRequired > totalAvailableStock;
  const isSelectionIncomplete =
    shouldFetchTerminals &&
    watchedTerminalCount > 0 &&
    selectedTerminalIDs.length < watchedTerminalCount;

  useEffect(() => {
    setSelectedTerminalIDs((prev) => {
      if (prev.length <= watchedTerminalCount) {
        return prev;
      }
      const assignedSet = new Set(assignedTerminalIDs);
      const assigned = prev.filter((id) => assignedSet.has(id));
      const unassigned = prev.filter((id) => !assignedSet.has(id));
      return [
        ...assigned,
        ...unassigned.slice(
          0,
          Math.max(0, watchedTerminalCount - assigned.length),
        ),
      ];
    });
  }, [watchedTerminalCount, assignedTerminalIDs]);

  const isIncompleteIconHidden = variant === "minimal";
  const contactSources = useContactSources(partyPublicID);

  const handleContactSourceSelect = ({
    responsiblePerson,
    phone,
    email,
  }: ContactSource) => {
    setValue("responsiblePerson", responsiblePerson, {
      shouldDirty: true,
    });
    setValue("phone", phone, { shouldDirty: true });
    setValue("email", email ?? "", { shouldDirty: true });
  };

  const isPublic = watch("isPublic");

  const {
    photoOutside,
    photoInside,
    hasPendingOutside,
    hasPendingInside,
    hasPendingChanges: hasPhotoChanges,
    isComplete: isPhotosComplete,
    setPendingPhoto,
    setPhotoError,
    uploadPendingPhotos,
    deletePhoto,
  } = useFacilityPhotos({
    merchantPublicID,
    facility,
    onUploadRejected: () => setExpandedSection("photos"),
  });

  // Pending photos are saved together with the form, so they count as unsaved changes
  // for the parent's close confirmation as well.
  useEffect(() => {
    onDirtyChange?.(isDirty || hasPhotoChanges);
  }, [isDirty, hasPhotoChanges, onDirtyChange]);

  const sectionCompletion = useFacilitySectionCompletion(watch, {
    hasExistingOutside: !!facility?.hasPhotoOutside,
    hasExistingInside: !!facility?.hasPhotoInside,
    hasPendingOutside,
    hasPendingInside,
  });

  const { mutateAsync: uploadFacilityLogo } = useUploadFacilityLogoMutation();
  const { mutateAsync: reserveTerminals } = useReserveTerminalsMutation({
    onError: (error: unknown) => toast.error(t(getReserveErrorResource(error))),
  });

  const reserveTerminalsIfSelected = async (
    facilityPublicID: string,
  ): Promise<boolean> => {
    if (isHunterFinanced && selectedTerminalIDs.length > 0) {
      const newTerminalIDs = selectedTerminalIDs.filter(
        (id) => !assignedTerminalIDs.includes(id),
      );
      if (newTerminalIDs.length > 0) {
        try {
          await reserveTerminals({
            merchantPublicID,
            facilityPublicID,
            terminalPublicIDs: newTerminalIDs,
          });
        } catch {
          return false;
        }
      }
    } else if (
      isLegione &&
      (watchedTerminalCount > 0 || watchedSoftPosCount > 0)
    ) {
      try {
        await reserveTerminals({
          merchantPublicID,
          facilityPublicID,
          terminalPublicIDs: null,
          terminalCount: watchedTerminalCount,
          softPosCount: watchedSoftPosCount,
        });
      } catch {
        return false;
      }
    }
    return true;
  };

  const { mutate: createMerchantFacility, isPending: isCreatePending } =
    useCreateMerchantFacilityMutation({
      onSuccess: async (data: MerchantFacilityDto) => {
        const photosOk = await uploadPendingPhotos(data.publicID);
        const reserveOk = await reserveTerminalsIfSelected(data.publicID);
        if (pendingLogoFile) {
          try {
            await uploadFacilityLogo({
              merchantPublicID,
              facilityPublicID: data.publicID,
              file: pendingLogoFile,
            });
          } catch {
            toast.error(t(LOGO_RESOURCES.UploadError));
          }
        }
        if (!photosOk) {
          // The facility row exists now — switch the open form to editing it, so a
          // retry re-saves this facility instead of creating a second one.
          reset(facilityDtoToFormModel(data));
          onFacilityCreated?.(data);
          toast.error(t(RESOURCES.PhotosNotSaved));
          return;
        }
        if (reserveOk) {
          toast.success(t(RESOURCES.AddSuccess));
          onSaveSuccessful();
        }
      },
      onError: () => toast.error(t(RESOURCES.AddError)),
    });

  const { mutate: updateMerchantFacility, isPending: isUpdatePending } =
    useUpdateMerchantFacilityMutation({
      onSuccess: async (data: MerchantFacilityDto) => {
        const photosOk = await uploadPendingPhotos(data.publicID);
        const reserveOk = await reserveTerminalsIfSelected(data.publicID);
        reset(facilityDtoToFormModel(data));
        if (!photosOk) {
          toast.error(t(RESOURCES.PhotosNotSaved));
          return;
        }
        if (reserveOk) {
          toast.success(t(RESOURCES.UpdateSuccess));
          onSaveSuccessful();
        }
      },
      onError: () => toast.error(t(RESOURCES.UpdateError)),
    });

  const onSubmit = (rawData: FacilityFormModel) => {
    if (hasUncommittedAddress) {
      toast.error(t(Resources.Components.Shared.AddressInput.UncommittedQuery));
      return;
    }

    const facilityPublicID = facility?.publicID;
    const data: CreateMerchantFacilityDto = {
      ...facility,
      ...rawData,
      ...(isLegione && {
        terminalCount: facility?.terminalCount ?? 0,
        softPosCount: facility?.softPosCount ?? 0,
      }),
      address: {
        ...rawData.address,
        type: AddressType.Headquarters,
      },
    };

    if (facilityPublicID) {
      if (!facility?.turnoverCurrencyCode) {
        // Type-narrowing guard for a branch that is unreachable in practice: an edited
        // facility always comes from the server, where the currency is a required field
        // (stamped at creation, backfilled by migration). Kept as a guard rather than a
        // cast so a broken client state refuses the save instead of sending a guess.
        toast.error(t(RESOURCES.UpdateError));
        return;
      }
      const payload: UpdateMerchantFacilityParameters = {
        publicID: merchantPublicID,
        data: {
          ...data,
          turnoverCurrencyCode: facility.turnoverCurrencyCode,
          publicID: facilityPublicID,
          isPublic: data.isPublic ?? false,
          categoryCodes: data.categoryCodes ?? [],
          hours: data.hours ?? [],
          specialHours: data.specialHours ?? [],
          hasPhotoOutside: facility?.hasPhotoOutside ?? false,
          hasPhotoInside: facility?.hasPhotoInside ?? false,
        },
      };
      updateMerchantFacility(payload);
    } else {
      const payload: CreateMerchantFacilityParameters = {
        publicID: merchantPublicID,
        data,
      };
      createMerchantFacility(payload);
    }
  };

  const isPending = isCreatePending || isUpdatePending;
  const isNewFacility = !facility?.publicID;
  const shouldShowCompletion = !isNewFacility || isSubmitted;

  const sectionHasErrors = (section: FacilitySection): boolean => {
    if (!isSubmitted) {
      return false;
    }
    return SECTION_FIELDS_MAP[section].some((field) => field in errors);
  };

  const toggleSection = (section: FacilitySection) =>
    setExpandedSection((prev) => (prev === section ? null : section));

  const expandNextSection = (section: FacilitySection) => {
    const index = SECTION_ORDER.indexOf(section);
    const next = SECTION_ORDER[index + 1];
    if (next) {
      setExpandedSection(next);
    }
  };

  const requiredSections = (
    <>
      <BasicInformationSection
        control={control}
        isExpanded={expandedSection === "basicInformation"}
        onToggle={() => toggleSection("basicInformation")}
        onNext={() => expandNextSection("basicInformation")}
        isComplete={
          shouldShowCompletion ? sectionCompletion.basicInformation : undefined
        }
        hasErrors={sectionHasErrors("basicInformation")}
      />

      <ContactSection
        control={control}
        contactSources={contactSources}
        onContactSourceSelect={handleContactSourceSelect}
        isExpanded={expandedSection === "contact"}
        onToggle={() => toggleSection("contact")}
        onNext={() => expandNextSection("contact")}
        isComplete={
          shouldShowCompletion ? sectionCompletion.contact : undefined
        }
        hasErrors={sectionHasErrors("contact")}
      />
    </>
  );

  const optionalSections = (
    <>
      <PublicFacilityInfoSection
        control={control}
        isExpanded={expandedSection === "publicFacilityInfo"}
        onToggle={() => toggleSection("publicFacilityInfo")}
        onNext={() => expandNextSection("publicFacilityInfo")}
        isPublic={isPublic}
        facilityCategoryOptions={facilityCategoryOptions}
        hasErrors={sectionHasErrors("publicFacilityInfo")}
      />

      <AddressSection
        control={control}
        setValue={setValue}
        trigger={trigger}
        isExpanded={expandedSection === "address"}
        onToggle={() => toggleSection("address")}
        onNext={() => expandNextSection("address")}
        isComplete={
          shouldShowCompletion ? sectionCompletion.address : undefined
        }
        isIncompleteIconHidden={isIncompleteIconHidden}
        hasErrors={sectionHasErrors("address")}
        companyHeadquartersAddress={companyHeadquartersAddress}
      />

      <TurnoverSection
        control={control}
        isExpanded={expandedSection === "turnover"}
        onToggle={() => toggleSection("turnover")}
        isComplete={
          shouldShowCompletion ? sectionCompletion.turnover : undefined
        }
        isIncompleteIconHidden={isIncompleteIconHidden}
        hasErrors={sectionHasErrors("turnover")}
        currencyCode={facility?.turnoverCurrencyCode ?? marketCurrencyCode}
      />

      {hasComgateProduct && (
        <TerminalSection
          control={control}
          isExpanded={expandedSection === "terminals"}
          onToggle={() => toggleSection("terminals")}
          onNext={() => expandNextSection("terminals")}
          isComplete={
            shouldShowCompletion ? sectionCompletion.terminals : undefined
          }
          hasErrors={sectionHasErrors("terminals")}
          isHunterFinanced={!!isHunterFinanced}
          isTerminalCountVisible={!!hasOrbitLProduct}
          isSoftPosCountVisible={!!hasSoftPosProduct}
          allTerminals={allTerminals}
          assignedTerminalIDs={assignedTerminalIDs}
          totalRequiredCount={effectiveTotalRequired}
          totalAvailableStock={totalAvailableStock}
          isLoadingTerminals={isLoadingTerminals}
          isTerminalsError={isTerminalsError}
          selectedTerminalIDs={selectedTerminalIDs}
          onSelectionChange={setSelectedTerminalIDs}
        />
      )}

      <PhotoSection
        isExpanded={expandedSection === "photos"}
        onToggle={() => toggleSection("photos")}
        isComplete={shouldShowCompletion ? isPhotosComplete : undefined}
        photoOutside={photoOutside}
        photoInside={photoInside}
        onPhotoChange={setPendingPhoto}
        onPhotoError={setPhotoError}
        onDeletePhoto={deletePhoto}
      />
    </>
  );

  const hasOnlyLogoChanged = isLogoChanged && !isDirty;

  // Logo changes are saved immediately via mutation (not deferred like form fields).
  // When only the logo changed (form not dirty), just signal completion.
  const handleLogoOnlySubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onSaveSuccessful();
  };

  return (
    <FormProvider {...formMethods}>
      <AddressUncommittedProvider value={uncommittedContextValue}>
        <form
          onSubmit={
            hasOnlyLogoChanged ? handleLogoOnlySubmit : handleSubmit(onSubmit)
          }
        >
          {variant === "minimal" ? (
            <>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                {t(RESOURCES.RequiredSection)}
              </Typography>
              <div>{requiredSections}</div>

              <Box mt={3} mb={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t(RESOURCES.OptionalSection)}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {t(RESOURCES.OptionalSectionDescription)}
                </Typography>
              </Box>
              <div>{optionalSections}</div>
            </>
          ) : (
            <div>
              {requiredSections}
              {optionalSections}
            </div>
          )}

          {facility?.publicID ? (
            <FacilityLogoUpload
              merchantPublicID={merchantPublicID}
              facilityPublicID={facility.publicID}
              logoFileName={facility.logoFileName}
              onLogoChange={() => setIsLogoChanged(true)}
            />
          ) : (
            <PendingLogoPicker
              file={pendingLogoFile}
              onFileChange={setPendingLogoFile}
            />
          )}

          <Box mt={3}>
            <PrimaryButton
              fullWidth
              type="submit"
              disabled={
                isStockExceeded ||
                isSelectionIncomplete ||
                (!isLogoChanged && !isDirty && !hasPhotoChanges)
              }
              isLoading={isPending}
              variant="contained"
              color="primary"
            >
              {t(Resources.Common.Save)}
            </PrimaryButton>
          </Box>
        </form>
      </AddressUncommittedProvider>
    </FormProvider>
  );
};
